import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Chip,
  Drawer,
  Divider,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search,
  Add,
  Refresh,
  Close,
  DirectionsBike,
  ShoppingBag,
  FiberManualRecord,
  PersonAdd,
  Phone,
  Email,
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  LocalShipping,
  Assignment,
} from "@mui/icons-material";
import { io } from "socket.io-client";
import api, { assignRiderToOrder, updateOrderStatus } from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";

/* ---------------------------------------------------------------
   CONSTANTS & HELPERS
--------------------------------------------------------------- */
const KES = (n) =>
  `KES ${Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 })}`;

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

const STATUS_META = {
  created:   { label: "Created",   color: "default"  },
  paid:      { label: "Paid",      color: "info"     },
  shipped:   { label: "Shipped",   color: "primary"  },
  completed: { label: "Completed", color: "success"  },
  cancelled: { label: "Cancelled", color: "error"    },
};

const statusChip = (status) => {
  const m = STATUS_META[status] || { label: status, color: "default" };
  return <Chip label={m.label} color={m.color} size="small" />;
};

const EMPTY_FORM = { name: "", email: "", phone: "", password: "" };

/* ---------------------------------------------------------------
   STAT CARD
--------------------------------------------------------------- */
function StatCard({ label, value, color, icon, sub }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} textTransform="uppercase">
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} color={color || "text.primary"} mt={0.5}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">{sub}</Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color || "primary"}.main`, width: 48, height: 48, opacity: 0.85 }}>
          {icon}
        </Avatar>
      </Stack>
    </Paper>
  );
}

/* ---------------------------------------------------------------
   MAIN COMPONENT
--------------------------------------------------------------- */
export default function ManageRiders() {
  const { showAlert } = useSnackbar();

  /* ---- data ---- */
  const [riders, setRiders]   = useState([]);
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  /* ---- search / filter ---- */
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  /* ---- add rider dialog ---- */
  const [addOpen, setAddOpen]       = useState(false);
  const [addForm, setAddForm]       = useState(EMPTY_FORM);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError]     = useState("");

  /* ---- rider detail drawer ---- */
  const [drawerRider, setDrawerRider] = useState(null);
  const [drawerOpen, setDrawerOpen]   = useState(false);

  /* ---- order detail drawer ---- */
  const [orderDrawer, setOrderDrawer]       = useState(false);
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [orderLoading, setOrderLoading]     = useState(false);

  /* ---- edit rider status ---- */
  const [editTarget, setEditTarget]   = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const socketRef = useRef(null);

  /* ---- load data ---- */
  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/api/admin/riders/with-orders");
      setRiders(res.data.riders || []);
      setOrders(res.data.orders || []);
    } catch (err) {
      setError("Failed to load riders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* ---- sockets ---- */
  useEffect(() => {
    const baseURL = api.defaults.baseURL?.replace(/\/+$/, "");
    if (!baseURL) return;
    const socket = io(baseURL, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("order:assigned", ({ order }) => {
      if (!order) return;
      setOrders((prev) =>
        prev.some((o) => o._id === order._id) ? prev.map((o) => o._id === order._id ? order : o) : [...prev, order]
      );
    });

    socket.on("order:unassigned", ({ orderId }) =>
      setOrders((prev) => prev.filter((o) => o._id !== orderId))
    );

    socket.on("rider:online",  ({ riderId }) =>
      setRiders((prev) => prev.map((r) => r._id === riderId ? { ...r, isOnline: true } : r))
    );

    socket.on("rider:offline", ({ riderId }) =>
      setRiders((prev) => prev.map((r) => r._id === riderId ? { ...r, isOnline: false, lastSeen: new Date().toISOString() } : r))
    );

    return () => socket.disconnect();
  }, []);

  /* ---- derived stats ---- */
  const stats = useMemo(() => {
    const total     = riders.length;
    const online    = riders.filter((r) => r.isOnline).length;
    const assigned  = riders.filter((r) => orders.some((o) => o.rider?._id === r._id)).length;
    const available = riders.filter((r) => r.isOnline && !orders.some((o) => o.rider?._id === r._id)).length;
    return { total, online, assigned, available };
  }, [riders, orders]);

  /* ---- filtered riders ---- */
  const filtered = useMemo(() => {
    let list = riders;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        `${r.name} ${r.phone} ${r.email}`.toLowerCase().includes(q)
      );
    }
    if (statusFilter === "online")   list = list.filter((r) => r.isOnline);
    if (statusFilter === "offline")  list = list.filter((r) => !r.isOnline);
    if (statusFilter === "assigned") list = list.filter((r) => orders.some((o) => o.rider?._id === r._id));
    if (statusFilter === "available") list = list.filter((r) => r.isOnline && !orders.some((o) => o.rider?._id === r._id));
    return list;
  }, [riders, orders, search, statusFilter]);

  /* ---- add rider ---- */
  const handleAddRider = async () => {
    if (!addForm.name.trim() || !addForm.phone.trim()) {
      setAddError("Name and phone are required.");
      return;
    }
    try {
      setAddLoading(true);
      setAddError("");
      await api.post("/api/users", { ...addForm, role: "rider" });
      showAlert("Rider added successfully.", "success");
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      load();
    } catch (err) {
      setAddError(err.response?.data?.message || "Failed to create rider.");
    } finally {
      setAddLoading(false);
    }
  };

  /* ---- open rider detail drawer ---- */
  const openRiderDrawer = (rider) => {
    setDrawerRider(rider);
    setDrawerOpen(true);
  };

  /* ---- load order detail ---- */
  const openOrderDrawer = async (orderId) => {
    setOrderDrawer(true);
    setOrderLoading(true);
    setSelectedOrder(null);
    try {
      const res = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(res.data);
    } catch (err) {
      showAlert("Could not load order details.", "error");
    } finally {
      setOrderLoading(false);
    }
  };

  /* ---- toggle active/inactive ---- */
  const handleToggleActive = async (rider) => {
    try {
      setEditTarget(rider._id);
      setEditLoading(true);
      await api.put(`/api/admin/riders/${rider._id}`, { active: !rider.active });
      showAlert(`Rider ${rider.active ? "deactivated" : "activated"}.`, "success");
      load();
    } catch (err) {
      showAlert("Failed to update rider status.", "error");
    } finally {
      setEditLoading(false);
      setEditTarget(null);
    }
  };

  /* ===========================================================
     RENDER
  =========================================================== */
  return (
    <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* PAGE HEADER */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">
            Riders Management
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            Monitor, assign, and manage your delivery fleet
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading} sx={{ border: "1px solid", borderColor: "divider" }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => { setAddOpen(true); setAddForm(EMPTY_FORM); setAddError(""); }}
            sx={{ fontWeight: 700 }}
          >
            Add Rider
          </Button>
        </Stack>
      </Stack>

      {/* STATS ROW */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={3}>
        <StatCard
          label="Total Riders"
          value={stats.total}
          color="primary"
          icon={<DirectionsBike />}
        />
        <StatCard
          label="Online Now"
          value={stats.online}
          color="success"
          icon={<FiberManualRecord />}
          sub="Active in the last session"
        />
        <StatCard
          label="Assigned to Orders"
          value={stats.assigned}
          color="warning"
          icon={<Assignment />}
        />
        <StatCard
          label="Available"
          value={stats.available}
          color="info"
          icon={<CheckCircle />}
          sub="Online & unassigned"
        />
      </Stack>

      {/* ERROR */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {/* FILTER BAR */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          placeholder="Search by name, phone or email…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Riders</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="available">Available</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" ml="auto">
          {filtered.length} rider{filtered.length !== 1 ? "s" : ""}
        </Typography>
      </Paper>

      {/* RIDERS TABLE */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Rider</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Active Orders</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Total Deliveries</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Seen</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No riders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => {
                    const riderOrders = orders.filter((o) => o.rider?._id === r._id);
                    const initials = r.name
                      ? r.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
                      : "R";

                    return (
                      <TableRow key={r._id} hover>
                        {/* Avatar + Name */}
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={1.5}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              badgeContent={
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: r.isOnline ? "success.main" : "text.disabled",
                                    border: "2px solid",
                                    borderColor: "background.paper",
                                  }}
                                />
                              }
                            >
                              <Avatar
                                src={r.image || ""}
                                sx={{ width: 40, height: 40, bgcolor: "primary.main", fontWeight: 700 }}
                              >
                                {!r.image && initials}
                              </Avatar>
                            </Badge>
                            <Box>
                              <Typography fontWeight={700} variant="body2">
                                {r.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {r.email || "—"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Contact */}
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Phone sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="body2">{r.phone || "—"}</Typography>
                          </Stack>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            icon={<FiberManualRecord sx={{ fontSize: "10px !important" }} />}
                            label={r.isOnline ? "Online" : "Offline"}
                            size="small"
                            color={r.isOnline ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>

                        {/* Active Orders */}
                        <TableCell align="center">
                          {riderOrders.length > 0 ? (
                            <Chip
                              label={riderOrders.length}
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 700 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">0</Typography>
                          )}
                        </TableCell>

                        {/* Total deliveries (from rider object if exists) */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {r.totalDeliveries ?? r.deliveries ?? "—"}
                          </Typography>
                        </TableCell>

                        {/* Last seen */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {r.isOnline
                              ? "Active now"
                              : r.lastSeen
                              ? new Date(r.lastSeen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" gap={0.5} justifyContent="flex-end">
                            <Tooltip title="View details & order history">
                              <IconButton size="small" onClick={() => openRiderDrawer(r)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={r.active === false ? "Activate rider" : "Deactivate rider"}>
                              <IconButton
                                size="small"
                                color={r.active === false ? "success" : "error"}
                                onClick={() => handleToggleActive(r)}
                                disabled={editLoading && editTarget === r._id}
                              >
                                {editLoading && editTarget === r._id
                                  ? <CircularProgress size={16} />
                                  : r.active === false
                                  ? <CheckCircle fontSize="small" />
                                  : <Cancel fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ============================================================
          ADD RIDER DIALOG
      ============================================================ */}
      <Dialog open={addOpen} onClose={() => !addLoading && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DirectionsBike color="primary" />
            Add New Rider
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Stack gap={2} mt={0.5}>
            <TextField
              label="Full Name"
              value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              disabled={addLoading}
            />
            <TextField
              label="Phone Number"
              value={addForm.phone}
              onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
              fullWidth
              required
              disabled={addLoading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
              }}
            />
            <TextField
              label="Email Address"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              disabled={addLoading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={addForm.password}
              onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
              fullWidth
              disabled={addLoading}
              helperText="Leave blank to auto-generate a temporary password"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={addLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddRider}
            disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {addLoading ? "Creating…" : "Create Rider"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================
          RIDER DETAIL DRAWER
      ============================================================ */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawerRider && (
          <>
            {/* Drawer header */}
            <Box
              sx={{
                p: 3,
                bgcolor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={800}>Rider Profile</Typography>
                <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                  <Close />
                </IconButton>
              </Stack>

              <Stack direction="row" alignItems="center" gap={2}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: drawerRider.isOnline ? "success.main" : "text.disabled",
                        border: "2px solid",
                        borderColor: "background.paper",
                      }}
                    />
                  }
                >
                  <Avatar
                    src={drawerRider.image || ""}
                    sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: 24, fontWeight: 800 }}
                  >
                    {!drawerRider.image &&
                      drawerRider.name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </Avatar>
                </Badge>
                <Box>
                  <Typography fontWeight={800} variant="h6">{drawerRider.name}</Typography>
                  <Stack direction="row" gap={1} mt={0.5} flexWrap="wrap">
                    <Chip
                      icon={<FiberManualRecord sx={{ fontSize: "10px !important" }} />}
                      label={drawerRider.isOnline ? "Online" : "Offline"}
                      size="small"
                      color={drawerRider.isOnline ? "success" : "default"}
                    />
                    {drawerRider.active === false && (
                      <Chip label="Inactive" size="small" color="error" />
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Phone sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2">{drawerRider.phone || "—"}</Typography>
                </Stack>
                {drawerRider.email && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Email sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">{drawerRider.email}</Typography>
                  </Stack>
                )}
              </Stack>

              {/* Summary chips */}
              <Stack direction="row" gap={1.5} mt={2}>
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" fontWeight={800} color="warning.main">
                    {orders.filter((o) => o.rider?._id === drawerRider._id).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Active Orders</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" fontWeight={800} color="success.main">
                    {drawerRider.totalDeliveries ?? drawerRider.deliveries ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Total Deliveries</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">Last Seen</Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {drawerRider.isOnline
                      ? "Now"
                      : drawerRider.lastSeen
                      ? new Date(drawerRider.lastSeen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Order history */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={2} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                Order History
              </Typography>

              {(() => {
                const rOrders = orders.filter((o) => o.rider?._id === drawerRider._id);
                if (rOrders.length === 0) {
                  return (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 6,
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        color: "text.secondary",
                      }}
                    >
                      <LocalShipping sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">No orders assigned yet</Typography>
                    </Box>
                  );
                }
                return (
                  <Stack gap={1}>
                    {rOrders.map((o) => (
                      <Paper
                        key={o._id}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          cursor: "pointer",
                          bgcolor: "background.paper",
                          "&:hover": { borderColor: "primary.main" },
                          transition: "border-color 0.2s",
                        }}
                        onClick={() => openOrderDrawer(o._id)}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" gap={1.5}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.light" }}>
                              <ShoppingBag sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box>
                              <Typography fontWeight={700} variant="body2">
                                Order #{o._id.slice(-6).toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {o.user?.name || "Customer"} • {o.items?.length || 0} item{o.items?.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack alignItems="flex-end" gap={0.5}>
                            {statusChip(o.status)}
                            <Typography variant="caption" color="text.secondary">
                              {fmt(o.createdAt)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                );
              })()}
            </Box>
          </>
        )}
      </Drawer>

      {/* ============================================================
          ORDER DETAIL DRAWER
      ============================================================ */}
      <Drawer
        anchor="right"
        open={orderDrawer}
        onClose={() => setOrderDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            bgcolor: "background.default",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={800}>Order Details</Typography>
            <IconButton size="small" onClick={() => setOrderDrawer(false)}>
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {orderLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {!orderLoading && selectedOrder && (
            <Stack gap={3}>
              {/* Status */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {statusChip(selectedOrder.status)}
              </Stack>

              {/* Customer */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Customer
                </Typography>
                <Typography fontWeight={700} mt={0.5}>{selectedOrder.user?.name || "—"}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedOrder.user?.phone || "—"}</Typography>
              </Paper>

              {/* Delivery address */}
              {selectedOrder.deliveryAddress && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                    Delivery Address
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {typeof selectedOrder.deliveryAddress === "string"
                      ? selectedOrder.deliveryAddress
                      : `${selectedOrder.deliveryAddress.street || ""}, ${selectedOrder.deliveryAddress.area || ""}`}
                  </Typography>
                </Paper>
              )}

              {/* Items */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Items ({selectedOrder.items?.length || 0})
                </Typography>
                <Stack gap={1}>
                  {(selectedOrder.items || []).map((it, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {it.product?.title || it.title || "Item"} × {it.quantity}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {KES(it.priceAtPurchase * it.quantity)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Total */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={700}>Total</Typography>
                <Typography fontWeight={800} variant="h6" color="primary.main">
                  {KES(selectedOrder.total)}
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Placed: {fmt(selectedOrder.createdAt)}
              </Typography>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
